import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';

import icon_call from './image/call.svg';
import icon_cancel from './image/cancel.svg';
import icon_db_check from './image/db-check.svg';
import icon_location from './image/location.svg';
import icon_mail from './image/mail.svg';
import icon_minus_gray400 from './image/minus-gray400.svg';
import icon_minus from './image/minus.svg';
import icon_phonecall from './image/phonecall.svg';
import icon_plus_gray400 from './image/plus-gray400.svg';
import icon_plus from './image/plus.svg';
import icon_question from './image/question.svg';
import icon_shipping from './image/shipping.svg';
import icon_speech_bubble from './image/speech_bubble.svg';
import icon_x_gray600 from './image/x-gray600.svg';

import { brand_color, gray100, gray300, gray400, gray900, white } from 'styles';
import { backgroundImage, imageSize, resetButton } from 'styles/mixin';

export enum FaceButtonColor {
  GRAY100 = 'GRAY100',
  GRAY900 = 'GRAY900',
  BRAND = 'BRAND',
}

export enum FaceButtonSize {
  W24_H24 = 'w24_h24',
  W32_H32 = 'w32_h32',
  S13_H32 = 's13_h32',
  S13_H40 = 's13_h40',
  S15_H40 = 's15_h40',
  S15_H48 = 's15_h48',
  S15_H56 = 's15_h56',
}

export enum FaceButtonIcon {
  SPEECH_BUBBLE = 'ic_speech_bubble',
  QUESTION = 'ic_question',
  SHIPPING = 'ic_shipping',
  CALL = 'ic_call',
  CANCEL = 'ic_cancel',
  DB_CHECK = 'ic_db_check',
  LOCATION = 'ic_location',
  MAIL = 'ic_mail',
  MINUS = 'ic_minus',
  PLUS = 'ic_plus',
  PHONECALL = 'ic_phonecall',
  X_GRAY600 = 'ic_x_gray600',
  WHITE_ZPAY = 'ic_white_zpay',
}

const colors: Record<FaceButtonColor, SerializedStyles> = {
  [FaceButtonColor.GRAY100]: css`
    background-color: ${gray100};
    color: ${gray900};
    &:disabled {
      color: ${gray400};
    }
  `,
  [FaceButtonColor.GRAY900]: css`
    background-color: ${gray900};
    color: ${white};
    &:disabled {
      color: ${gray300};
    }
  `,
  [FaceButtonColor.BRAND]: css`
    color: ${white};
    background-color: ${brand_color};
  `,
};

const sizes: Record<FaceButtonSize, SerializedStyles> = {
  [FaceButtonSize.W24_H24]: css`
    width: 24px;
    height: 24px;
    border-radius: 12px;
  `,
  [FaceButtonSize.W32_H32]: css`
    width: 32px;
    height: 32px;
  `,
  [FaceButtonSize.S13_H32]: css`
    height: 32px;
    padding: 0 12px;
    border-radius: 16px;
  `,
  [FaceButtonSize.S13_H40]: css`
    height: 40px;
    padding: 0 16px;
    border-radius: 20px;
  `,
  [FaceButtonSize.S15_H40]: css`
    height: 40px;
    border-radius: 20px;
  `,
  [FaceButtonSize.S15_H48]: css`
    height: 48px;
    border-radius: 24px;
  `,
  [FaceButtonSize.S15_H56]: css`
    height: 56px;
    border-radius: 28px;
  `,
};

export const Container = styled.button<{ colorType?: FaceButtonColor; sizeType?: FaceButtonSize }>`
  ${resetButton}
  max-width: 100%;
  overflow: hidden;
  border-radius: 4px;

  &::before {
    content: '';
    height: 100%;
  }

  &:disabled {
    color: ${white};
    background-color: ${gray300};
  }

  ${({ colorType }) => colorType && colors[colorType]}
  ${({ sizeType }) => sizeType && sizes[sizeType]}

  /* icons */
  > .icon {
    background-color: transparent;
  }
  &::before,
  > .icon,
  > .text {
    display: inline-block;
    vertical-align: middle;
  }
  > .text {
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > .ic_speech_bubble,
  > .ic_shipping,
  > .ic_white_zpay,
  > .ic_db_check {
    ${imageSize('24px', '24px')}
  }
  > .ic_white_zpay,
  > .ic_db_check {
    margin-right: 4px;
    + .text {
      max-width: calc(100% - 28px);
    }
  }
  > .ic_white_zpay {
    ${backgroundImage({
      size1x: require('./image/white-zpay@1x.png'),
      size2x: require('./image/white-zpay@2x.png'),
      size3x: require('./image/white-zpay@3x.png'),
    })}
  }
  > .ic_db_check {
    ${backgroundImage({
      size1x: icon_db_check,
    })}
  }
  > .ic_speech_bubble,
  > .ic_shipping {
    margin-right: 2px;
    + .text {
      max-width: calc(100% - 26px);
    }
  }
  > .ic_white_zpay {
    padding-bottom: 3px;
  }
  > .ic_question {
    background-image: url(${icon_question});
  }
  > .ic_speech_bubble {
    background-image: url(${icon_speech_bubble});
  }
  > .ic_shipping {
    background-image: url(${icon_shipping});
  }
  > .ic_call,
  > .ic_phonecall,
  > .ic_mail,
  > .ic_cancel,
  > .ic_plus,
  > .ic_minus,
  > .ic_x_gray600,
  > .ic_location,
  > .ic_question {
    ${imageSize('16px', '16px')}
  }
  > .ic_call,
  > .ic_phonecall,
  > .ic_mail,
  > .ic_location {
    margin-right: 4px;
  }
  > .ic_plus {
    background-image: url(${icon_plus});
  }
  > .ic_minus {
    background-image: url(${icon_minus});
  }
  > .ic_cancel {
    background-image: url(${icon_cancel});
  }
  > .ic_x_gray600 {
    background-image: url(${icon_x_gray600});
  }
  > .ic_call {
    background-image: url(${icon_call});
  }
  > .ic_phonecall {
    background-image: url(${icon_phonecall});
  }
  > .ic_mail {
    background-image: url(${icon_mail});
  }
  > .ic_location {
    background-image: url(${icon_location});
  }
  &[disabled] {
    > .ic_plus {
      background-image: url(${icon_plus_gray400});
    }
    > .ic_minus {
      background-image: url(${icon_minus_gray400});
    }
  }
`;
